'use client';

import React, { useEffect, useState } from 'react';
import { Link } from '@/components/link/Link';
import { NotFoundWrapper } from '@/components/not-found-wrapper/NotFoundWrapper';
import type { LocaleLanguage } from '@/lib/locale-context';
import { useParams } from 'next/navigation';

// Client-side dictionary loader with fallback
const loadDictionary = async (locale: LocaleLanguage) => {
  try {
    const dict = await import(`@/dictionaries/${locale}.json`);
    return dict.default;
  } catch (error) {
    console.error('Failed to load dictionary:', error);
    return null;
  }
};

const NotFound: React.FC = () => {
  const [translations, setTranslations] = useState<any>(null);
  const params = useParams();

  // Assert locale is 'de' or 'en' or fallback to 'de'
  const locale =
    params?.locale === 'de' || params?.locale === 'en' ? params.locale : 'de';

  useEffect(() => {
    async function loadData() {
      const dict = await loadDictionary(locale);
      setTranslations(dict);
    }
    loadData();
  }, [locale]);

  const gradientStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(180deg, #07314C 30%, #F8F9FB 100%)',
  };

  return (
    <NotFoundWrapper locale={locale}>
      <div className="flex flex-col items-center justify-center bg-light-grey pb-28 pt-24 sm:pb-40 sm:pt-32">
        <h1
          className="bg-clip-text text-9xl font-medium text-transparent sm:text-[220px]"
          style={gradientStyle}
        >
          404
        </h1>
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex flex-col gap-3">
            <p className="text-xl font-medium text-brand-700 sm:text-2xl">
              {translations?.notFound?.title || 'Ups! Seite nicht gefunden'}
            </p>
            <p className="font-normal text-neutral-700 sm:text-lg">
              {translations?.notFound?.description ||
                'Wir konnten die Seite, die Sie suchen, leider nicht finden.'}
            </p>
          </div>
          <Link
            href={`/${locale}/`}
            variant="secondary"
            className="w-fit"
            title={translations?.notFound?.backToHome || 'Back to Home'}
          >
            {translations?.notFound?.backToHome || 'Zur Startseite'}
          </Link>
        </div>
        <div className="gap mt-16 flex flex-col items-center gap-6">
          <div className="font-medium text-brand-700 sm:text-lg">
            <p>
              {translations?.notFound?.tryTheseLinks ||
                'Versuchen Sie einen der folgenden Links, der Ihnen hoffentlich weiterhilft:'}
            </p>
          </div>
          <ul className="flex flex-col gap-2 text-center">
            {translations?.notFound?.links?.map(
              (link: { label: string; href: string }, i: number) => (
                <li key={i}>
                  <Link
                    href={`/${locale}${link.href}`}
                    className="justify-center text-brand-500 underline underline-offset-4"
                    variant="tertiary"
                  >
                    {link.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </div>
      </div>
    </NotFoundWrapper>
  );
};

export default NotFound;
